<template>
  <div id="SectionCompleteSolution">
    <UIContainer class="SectionCompleteSolution__container">
      <template #left>
        <div class="SectionCompleteSolution__image-container">
          <FramedImage
            class="SectionCompleteSolution__framed"
            :src="platformImage"
            alt="B Corp platform preview"
          />
        </div>
      </template>

      <template #right>
        <div class="SectionCompleteSolution__content">
          <SectionTitle titleSize="sm">
            <template #title> Your complete solution for B Corp </template>

            <template #description>
              We provide the integrated platform and expert support you need to
              meet the new mandatory B Corp climate requirements with
              confidence.
            </template>
          </SectionTitle>

          <div class="SectionCompleteSolution__features">
            <div
              class="SectionCompleteSolution__feature"
              v-for="feature in features"
              :key="feature"
            >
              <UIIcon
                class="SectionCompleteSolution__feature-icon"
                name="checkCircle"
              />
              <span>{{ feature }}</span>
            </div>
          </div>

          <p class="SectionCompleteSolution__description">
            We deliver this through a single, governed software platform,
            including powerful decarbonization modeling, plus support from our
            team of B Corp and SBTi experts.
          </p>
        </div>
      </template>
    </UIContainer>
  </div>
</template>

<script setup lang="ts">
import SectionTitle from '@/views/components/misc/SectionTitle.vue'
import UIContainer from '@/views/components/ui/containers/UIContainer/UIContainer.vue'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'
import FramedImage from '@/views/components/misc/FramedImage.vue'
import platformImage from '../assets/completeSolution/platform.avif'

const features = [
  'Measure a full scope carbon footprint in line with the GHG Protocol.',
  'Set a science-based Net Zero target (SBTi-aligned) with our powerful decarbonization modeling tools.',
  'Build a defensible Climate Transition Plan that stands up to auditor scrutiny and is ready for disclosure.',
  'Fund verified, high-integrity offsetting projects through our curated carbon removal and avoidance programs.',
]
</script>

<style lang="scss" scoped>
#SectionCompleteSolution {
  position: relative;

  .SectionCompleteSolution__header-container {
    margin-bottom: 2rem;
    padding-bottom: 0;

    @include md {
      max-width: 48rem;
      text-align: center;
      margin-bottom: 6rem;
    }
  }

  .SectionCompleteSolution__container {
    padding-top: 0;
  }

  .SectionCompleteSolution__image-container {
    position: absolute;
    display: none;
    height: 100%;

    @include md {
      display: block;
      position: absolute;
      right: 0;
      padding: 3.5rem 0rem 3.5rem 0;
    }
  }

  .SectionCompleteSolution__content {
    padding-left: 0rem;

    @include md {
      padding-left: 6rem;
    }
  }

  .SectionCompleteSolution__title {
    @include font-size-20px();
    font-weight: 600;
    line-height: 1.2;
    color: $color-ui-gray-cool-900;
    margin-bottom: 1rem;

    @include md {
      @include font-size-24px();
    }
  }

  .SectionCompleteSolution__features {
    display: grid;
    gap: 1.25rem;
    padding-left: 1rem;
    margin-top: 2rem;
    margin-bottom: 2rem;
  }

  .SectionCompleteSolution__feature {
    @include font-size-18px();
    display: flex;
    align-items: center;
    color: $color-ui-gray-cool-600;
    gap: 0.75rem;
  }

  .SectionCompleteSolution__feature-icon {
    --size: 1.75rem;
  }

  .SectionCompleteSolution__cta {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .SectionCompleteSolution__description {
    @include font-size-18px();
  }
}
</style>
